<template>
  <div class="search-box">
    <el-input 
      v-model="searchText" 
      placeholder="搜索指标" 
      size="small"
      clearable
      @focus="onFocus"
      @blur="onBlur"
    >
      <template #suffix>
        <el-icon><search /></el-icon>
      </template>
    </el-input>
    
    <!-- 搜索下拉框 -->
    <div 
      v-show="searchDropdownVisible" 
      class="search-dropdown"
      @mousedown.prevent
    >
      <ul class="dropdown-list">
        <li 
          v-for="(item, index) in searchResults" 
          :key="index"
          class="dropdown-item"
          @click="onResultClick(item)"
        >
          <span v-html="highlightText(item.label, searchText)"></span>
        </li>
        <li v-if="searchResults.length === 0" class="dropdown-item empty-item">
          无匹配结果
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'IndicatorSearch',
  props: {
    indicatorTreeData: {
      type: Array,
      required: true
    }
  },
  emits: ['select'],
  components: {
    Search
  },
  setup(props, { emit }) {
    const searchText = ref('')
    const searchDropdownVisible = ref(false)
    const searchResults = ref([])

    // 防抖函数
    function debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
    
    // 搜索功能
    const performSearch = (query) => {
      if (!query.trim()) {
        searchResults.value = [];
        searchDropdownVisible.value = false;
        return;
      }
      
      // 递归搜索所有节点，收集所有匹配的叶子节点
      const results = [];
      const searchNodes = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) {
          return;
        }
        nodes.forEach(node => {
          // 如果是叶子节点（指标节点）且匹配查询
          if ((!node.children || node.children.length === 0) && 
              node.label && node.label.toLowerCase().includes(query.toLowerCase())) {
            // 在指标树中查找对应的节点并同步收藏状态
            const syncFavoriteStatus = (treeNodes) => {
              treeNodes.forEach(treeNode => {
                if (treeNode.id === node.id) {
                  // 同步收藏状态
                  node.favorites = treeNode.favorites || 0;
                }
                if (treeNode.children && treeNode.children.length) {
                  syncFavoriteStatus(treeNode.children);
                }
              });
            };
            
            syncFavoriteStatus(props.indicatorTreeData);
            results.push({...node}); // 创建副本避免直接引用
          }
          // 递归搜索子节点
          if (node.children && node.children.length) {
            searchNodes(node.children);
          }
        });
      };
      
      searchNodes(props.indicatorTreeData);
      searchResults.value = results.slice(0, 10); // 最多显示10个结果
      searchDropdownVisible.value = searchResults.value.length > 0;
    };
    
    // 防抖处理搜索输入
    const debouncedSearch = debounce((query) => {
      performSearch(query);
    }, 300);

    // 监听搜索文本变化
    watch(searchText, (newVal) => {
      debouncedSearch(newVal);
    });

    // 处理搜索框获得焦点
    const onFocus = () => {
      if (searchText.value && searchResults.value && searchResults.value.length > 0) {
        searchDropdownVisible.value = true;
      }
    };

    // 处理搜索框失去焦点
    const onBlur = () => {
      // 延迟隐藏，以便点击下拉项能生效
      setTimeout(() => {
        searchDropdownVisible.value = false;
      }, 200);
    };

    // 高亮文本中的关键词
    const highlightText = (text, query) => {
      if (!query || !text) return text;
      
      // 转义特殊字符以避免正则表达式错误
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      
      return text.replace(regex, '<mark class="highlight-mark">$1</mark>');
    };

    // 处理下拉项点击
    const onResultClick = (item) => {
      // 发送选中事件给父组件
      emit('select', item);
      // 清空搜索框并隐藏下拉框
      searchText.value = '';
      searchDropdownVisible.value = false;
      searchResults.value = [];
    };

    return {
      searchText,
      searchDropdownVisible,
      searchResults,
      onFocus,
      onBlur,
      onResultClick,
      highlightText
    }
  }
}
</script>

<style scoped src="./IndicatorSearch.css"></style>